import rest from '@/lib/rest';
import type { Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { defineStore } from 'pinia';
import { useUserStore } from './user';

interface PaymentState {
  stripe: Stripe | null;
  elements: StripeElements | null;
  paymentElement: StripePaymentElement | null;
  clientSecret: string;
  customerSessionClientSecret: string;
  saving: boolean;
  error: string | null;
}
export const usePaymentStore = defineStore('payment', {
  state: (): PaymentState => ({
    stripe: null,
    elements: null,
    paymentElement: null,
    clientSecret: '',
    customerSessionClientSecret: '',
    saving: false,
    error: null,
  }),

  actions: {
    async initializeStripe(publishableKey: string) {
      const stripe = await loadStripe(publishableKey);
      if (!stripe) {
        throw new Error('Failed to initialize Stripe');
      }
      this.stripe = stripe;
      return stripe;
    },

    async createSetupIntent() {
      try {
        const userStore = useUserStore();
        const response: any = await rest
          .post('/billing/payment-methods/setup')
          .header('x-uid', userStore.user?.uid || '')
          .credentials('include')
          .send();
        this.clientSecret = response.clientSecret;
        this.customerSessionClientSecret = response.customerSessionClientSecret ?? '';
        return this.clientSecret;
      } catch (error) {
        this.error = 'Failed to create setup intent';
        throw error;
      }
    },

    async confirmSetup(returnUrl: string) {
      if (!this.stripe || !this.elements) {
        throw new Error('Stripe not initialized');
      }

      this.saving = true;
      try {
        const { error } = await this.stripe.confirmSetup({
          elements: this.elements,
          clientSecret: this.clientSecret,
          confirmParams: { return_url: returnUrl },
          redirect: 'if_required',
        });

        if (error) {
          if (error.type === 'card_error' || error.type === 'validation_error') {
            this.error = error.message || 'An unexpected error occurred';
          } else {
            this.error = 'An unexpected error occurred';
          }
          throw error;
        }
        const userStore = useUserStore();

        // Set as default payment method
        await rest
          .post('/billing/payment-methods/last/default')
          .header('x-uid', userStore.user?.uid || '')
          .credentials('include')
          .send();

        return true;
      } catch (error) {
        throw error;
      } finally {
        this.saving = false;
      }
    },

    setElements(elements: StripeElements) {
      this.elements = elements;
    },

    setPaymentElement(element: StripePaymentElement) {
      this.paymentElement = element;
    },

    /** Destroy mounted Elements UI and clear secrets (e.g. modal closed or component unmounted). */
    teardownPaymentForm() {
      if (this.paymentElement) {
        try {
          this.paymentElement.destroy();
        } catch {
          /* ignore */
        }
      }
      this.paymentElement = null;
      this.elements = null;
      this.clientSecret = '';
      this.customerSessionClientSecret = '';
    },

    clearError() {
      this.error = null;
    },
  },
});
